import React, { useEffect, useMemo, useState } from 'react';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import KeyboardArrowRightOutlinedIcon from '@mui/icons-material/KeyboardArrowRightOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { fetchDeveloperApiCatalog } from '../services/developerApiService';
import '../assets/styles/DeveloperApiCatalog.scss';

const METHOD_CLASS = {
  GET: 'is-get', POST: 'is-post', PUT: 'is-put', PATCH: 'is-patch', DELETE: 'is-delete',
};

const pretty = (value) => String(value || '')
  .replaceAll('_', ' ')
  .toLowerCase()
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

const copyText = (value) => navigator.clipboard?.writeText(String(value || ''));

const JsonBlock = ({ value }) => {
  if (!value) return <span className="dev-api-empty">No JSON body</span>;
  const content = JSON.stringify(value, null, 2);
  return (
    <div className="dev-api-code-wrap">
      <button type="button" className="dev-api-copy" onClick={() => copyText(content)} title="Copy payload">
        <ContentCopyOutlinedIcon fontSize="small" />
      </button>
      <pre>{content}</pre>
    </div>
  );
};

const EndpointRow = ({ endpoint, open, onToggle }) => (
  <article className={`dev-api-endpoint ${open ? 'is-open' : ''}`}>
    <button type="button" className="dev-api-endpoint__summary" onClick={onToggle}>
      <span className="dev-api-caret">{open ? <KeyboardArrowDownOutlinedIcon /> : <KeyboardArrowRightOutlinedIcon />}</span>
      <span className={`dev-api-method ${METHOD_CLASS[endpoint.method] || ''}`}>{endpoint.method}</span>
      <code>{endpoint.path}</code>
      <span className="dev-api-endpoint__module">{endpoint.module}</span>
      <span className={`dev-api-audience ${endpoint.audience === 'PUBLIC_STOREFRONT' ? 'is-public' : endpoint.adminOnly ? 'is-admin' : ''}`}>
        {pretty(endpoint.audience)}
      </span>
    </button>

    {open && (
      <div className="dev-api-endpoint__details">
        <div className="dev-api-detail-grid">
          <div>
            <span>Authentication</span>
            <strong>{endpoint.adminOnly ? 'JWT + Admin' : endpoint.auth === 'JWT' ? 'JWT' : 'None'}</strong>
          </div>
          <div>
            <span>Payload source</span>
            <strong>{pretty(endpoint.payloadSource)}</strong>
          </div>
          <div>
            <span>Path parameters</span>
            <strong>{endpoint.pathParams?.length ? endpoint.pathParams.join(', ') : 'None'}</strong>
          </div>
          <div>
            <span>Query parameters</span>
            <strong>{endpoint.queryParams?.length ? endpoint.queryParams.join(', ') : 'None detected'}</strong>
          </div>
          <div>
            <span>Headers</span>
            <strong>{endpoint.headers?.length ? endpoint.headers.join(', ') : 'Standard headers'}</strong>
          </div>
          <div>
            <span>Handler</span>
            <strong>{endpoint.handlers?.length ? endpoint.handlers.join(', ') : 'Inline handler'}</strong>
          </div>
        </div>

        {endpoint.audience === 'INTERNAL_UNGUARDED' && (
          <div className="dev-api-warning">
            <WarningAmberOutlinedIcon fontSize="small" />
            This active canonical route has no JWT middleware at route level. Treat it as an internal security review item, not a storefront-public contract.
          </div>
        )}

        {endpoint.requestBody && (
          <div className="dev-api-payload">
            <div className="dev-api-subheading">
              <div><strong>Request body</strong><span>JSON payload format</span></div>
            </div>
            <JsonBlock value={endpoint.requestBody} />
          </div>
        )}
      </div>
    )}
  </article>
);

const StatusView = ({ catalog }) => (
  <div className="dev-api-status-view">
    <section className="dev-api-architecture-strip">
      <div><span>Release conditions</span><div>{(catalog.releaseConditions || []).map((item) => <code key={item}>{item}</code>)}</div></div>
      <div><span>Fulfilment modes</span><div>{(catalog.fulfilmentModes || []).map((item) => <code key={item}>{item}</code>)}</div></div>
    </section>

    <section className="dev-api-section">
      <div className="dev-api-section__heading">
        <div><h2>Canonical module statuses</h2><p>Only modules with a real workflow/state lifecycle are listed here.</p></div>
      </div>
      <div className="dev-api-status-grid">
        {(catalog.statusModules || []).map((item) => (
          <article className="dev-api-status-card" key={item.key}>
            <h3>{item.module}</h3>
            <div className="dev-api-status-chips">{(item.statuses || []).map((status) => <span key={status}>{status}</span>)}</div>
            <p>{item.handoff}</p>
            {!!item.next?.length && <div className="dev-api-next"><span>Hands off to</span><strong>{item.next.join(' → ')}</strong></div>}
          </article>
        ))}
      </div>
    </section>

    <section className="dev-api-section">
      <div className="dev-api-section__heading">
        <div><h2>Cross-module handoffs</h2><p>The canonical event/action that moves responsibility from one module to the next.</p></div>
      </div>
      <div className="dev-api-handoff-table-wrap">
        <table className="dev-api-handoff-table">
          <thead><tr><th>From</th><th>Event / Action</th><th>To</th><th>Rule</th></tr></thead>
          <tbody>
            {(catalog.handoffs || []).map((row, index) => (
              <tr key={`${row.from}-${row.to}-${index}`}>
                <td><strong>{row.from}</strong></td>
                <td><code>{row.event}</code></td>
                <td><strong>{row.to}</strong></td>
                <td>{row.rule}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  </div>
);

export default function DeveloperApiCatalog() {
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('apis');
  const [search, setSearch] = useState('');
  const [area, setArea] = useState('ALL');
  const [method, setMethod] = useState('ALL');
  const [audience, setAudience] = useState('ALL');
  const [openIds, setOpenIds] = useState(() => new Set());

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      setCatalog(await fetchDeveloperApiCatalog());
    } catch (requestError) {
      setError(requestError.response?.data?.error || requestError.message || 'Failed to load the API catalog.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const areas = useMemo(() => ['ALL', ...new Set((catalog?.endpoints || []).map((item) => item.area))], [catalog]);
  const audiences = useMemo(() => ['ALL', ...new Set((catalog?.endpoints || []).map((item) => item.audience))], [catalog]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (catalog?.endpoints || []).filter((item) => {
      if (area !== 'ALL' && item.area !== area) return false;
      if (method !== 'ALL' && item.method !== method) return false;
      if (audience !== 'ALL' && item.audience !== audience) return false;
      if (!needle) return true;
      return [item.method, item.path, item.module, item.area, item.audience, ...(item.queryParams || []), ...(item.pathParams || [])]
        .join(' ').toLowerCase().includes(needle);
    });
  }, [catalog, search, area, method, audience]);

  const grouped = useMemo(() => filtered.reduce((acc, endpoint) => {
    const key = endpoint.area || 'Other';
    if (!acc[key]) acc[key] = [];
    acc[key].push(endpoint);
    return acc;
  }, {}), [filtered]);

  const toggle = (id) => setOpenIds((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  return (
    <main className="dev-api-page">
      <header className="dev-api-header">
        <div>
          <span className="dev-api-eyebrow">Developer tools</span>
          <h1>API Catalog & Architecture</h1>
          <p>Canonical JDH APIs for CRM and storefront integrations. Compatibility and legacy adapters are intentionally excluded.</p>
        </div>
        <button type="button" className="dev-api-refresh" onClick={load} disabled={loading}>
          <RefreshOutlinedIcon fontSize="small" /> {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </header>

      {error && <div className="dev-api-error">{error}</div>}

      {catalog && (
        <>
          <section className="dev-api-summary">
            <div><span>Architecture</span><strong>{catalog.architecture}</strong></div>
            <div><span>Active canonical endpoints</span><strong>{catalog.endpointCount}</strong></div>
            <div><span>API areas</span><strong>{Object.keys(catalog.moduleCounts || {}).length}</strong></div>
            <div><span>Generated</span><strong>{new Date(catalog.generatedAt).toLocaleString()}</strong></div>
          </section>

          <div className="dev-api-tabs" role="tablist">
            <button type="button" className={tab === 'apis' ? 'is-active' : ''} onClick={() => setTab('apis')}>API Catalog</button>
            <button type="button" className={tab === 'statuses' ? 'is-active' : ''} onClick={() => setTab('statuses')}>Statuses & Handoffs</button>
          </div>

          {tab === 'apis' ? (
            <>
              <section className="dev-api-toolbar">
                <label className="dev-api-search"><SearchOutlinedIcon fontSize="small" /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search path, module, parameter…" /></label>
                <select value={area} onChange={(event) => setArea(event.target.value)}>{areas.map((item) => <option value={item} key={item}>{item === 'ALL' ? 'All areas' : item}</option>)}</select>
                <select value={method} onChange={(event) => setMethod(event.target.value)}>{['ALL', 'GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((item) => <option value={item} key={item}>{item === 'ALL' ? 'All methods' : item}</option>)}</select>
                <select value={audience} onChange={(event) => setAudience(event.target.value)}>{audiences.map((item) => <option value={item} key={item}>{item === 'ALL' ? 'All audiences' : pretty(item)}</option>)}</select>
              </section>

              <div className="dev-api-scope-note">{catalog.scope}</div>

              {Object.entries(grouped).map(([groupName, endpoints]) => (
                <section className="dev-api-section" key={groupName}>
                  <div className="dev-api-section__heading"><div><h2>{groupName}</h2><p>{endpoints.length} endpoint{endpoints.length === 1 ? '' : 's'}</p></div></div>
                  <div className="dev-api-endpoints">
                    {endpoints.map((endpoint) => <EndpointRow key={endpoint.id} endpoint={endpoint} open={openIds.has(endpoint.id)} onToggle={() => toggle(endpoint.id)} />)}
                  </div>
                </section>
              ))}

              {!filtered.length && !loading && <div className="dev-api-empty-state">No canonical APIs match these filters.</div>}
            </>
          ) : <StatusView catalog={catalog} />}
        </>
      )}

      {loading && !catalog && <div className="dev-api-loading">Loading active API registry…</div>}
    </main>
  );
}
